import { useState, useMemo } from "react";
import { Button } from "./ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./ui/dialog";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Textarea } from "./ui/textarea";
import { Printer, FileText } from "lucide-react";

const _formatDateRo = (iso) => {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return iso;
    const dd = String(d.getDate()).padStart(2, "0");
    const mm = String(d.getMonth() + 1).padStart(2, "0");
    return `${dd}.${mm}.${d.getFullYear()}`;
  } catch {
    return iso;
  }
};

export const AdeverintaGeneralaButton = ({ patient }) => {
  const [open, setOpen] = useState(false);
  const dp = patient?.date_personale || {};
  const is = patient?.info_sportive || {};

  // Câmpuri prepopulate din datele pacientului — editabile la nevoie
  const [form, setForm] = useState({
    unitate_medicala: "Cabinet Medical Sportiv",
    nr_fisa: String(patient?.numar_viza || patient?.numar_ordine || ""),
    data_fisa: _formatDateRo(new Date().toISOString()),
    nume_complet: `${dp.nume || ""} ${dp.prenume || ""}`.trim(),
    sex: dp.sex === "Feminin" ? "F" : dp.sex === "Masculin" ? "M" : "",
    cnp: dp.cnp || "",
    data_nasterii: _formatDateRo(dp.data_nasterii),
    judet: dp.judet || "",
    localitate: dp.localitate || "",
    ocupatia: "",
    club: is.club_sportiv || "",
    suferind_de: "",
    se_recomanda: "",
    pentru_a_servi: "",
    data_eliberarii: _formatDateRo(new Date().toISOString()),
  });

  const f = (k, v) => setForm((p) => ({ ...p, [k]: v }));
  const handlePrint = () => window.print();

  return (
    <>
      <Button
        variant="outline"
        onClick={() => setOpen(true)}
        data-testid="adeverinta-generala-btn"
        className="w-full justify-start gap-2 border-slate-300 hover:bg-slate-50"
      >
        <FileText className="w-4 h-4" />
        Generează Adeverință Medicală Generală
      </Button>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-3xl max-h-[92vh] overflow-y-auto print:max-w-none print:max-h-none print:overflow-visible">
          <DialogHeader className="print:hidden">
            <DialogTitle className="flex items-center justify-between">
              <span>Adeverință Medicală — generală</span>
              <Button size="sm" onClick={handlePrint} className="gap-2" data-testid="adeverinta-generala-print">
                <Printer className="w-4 h-4" /> Tipărește
              </Button>
            </DialogTitle>
          </DialogHeader>

          {/* Editor compact (vizibil doar pe ecran) */}
          <div className="space-y-3 print:hidden border-b pb-4">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs">Unitatea medicală</Label>
                <Input value={form.unitate_medicala} onChange={(e) => f("unitate_medicala", e.target.value)} data-testid="adev-gen-unitate" />
              </div>
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <Label className="text-xs">Nr. fișă</Label>
                  <Input value={form.nr_fisa} onChange={(e) => f("nr_fisa", e.target.value)} data-testid="adev-gen-nr" />
                </div>
                <div>
                  <Label className="text-xs">Data fișă</Label>
                  <Input value={form.data_fisa} onChange={(e) => f("data_fisa", e.target.value)} data-testid="adev-gen-data-fisa" />
                </div>
              </div>
            </div>

            {/* Date sportiv — toate editabile */}
            <div className="grid grid-cols-2 gap-3">
              <div className="col-span-2">
                <Label className="text-xs">Nume și prenume</Label>
                <Input value={form.nume_complet} onChange={(e) => f("nume_complet", e.target.value)} data-testid="adev-gen-nume" />
              </div>
              <div>
                <Label className="text-xs">Sex</Label>
                <Input value={form.sex} onChange={(e) => f("sex", e.target.value)} placeholder="M sau F" data-testid="adev-gen-sex" />
              </div>
              <div>
                <Label className="text-xs">CNP</Label>
                <Input value={form.cnp} onChange={(e) => f("cnp", e.target.value)} data-testid="adev-gen-cnp" />
              </div>
              <div>
                <Label className="text-xs">Data nașterii</Label>
                <Input value={form.data_nasterii} onChange={(e) => f("data_nasterii", e.target.value)} placeholder="dd.mm.yyyy" data-testid="adev-gen-data-n" />
              </div>
              <div>
                <Label className="text-xs">Județ</Label>
                <Input value={form.judet} onChange={(e) => f("judet", e.target.value)} data-testid="adev-gen-judet" />
              </div>
              <div>
                <Label className="text-xs">Localitate</Label>
                <Input value={form.localitate} onChange={(e) => f("localitate", e.target.value)} data-testid="adev-gen-loc" />
              </div>
              <div>
                <Label className="text-xs">Data eliberării</Label>
                <Input value={form.data_eliberarii} onChange={(e) => f("data_eliberarii", e.target.value)} placeholder="dd.mm.yyyy" data-testid="adev-gen-data-elib" />
              </div>
            </div>

            <div className="p-3 bg-amber-50 border-2 border-amber-300 rounded">
              <Label className="text-sm font-semibold text-amber-900">
                Având ocupația de: <span className="text-xs font-normal">(câmp principal de completat)</span>
              </Label>
              <Input
                data-testid="ocupatia-input"
                value={form.ocupatia}
                onChange={(e) => f("ocupatia", e.target.value)}
                placeholder="ex: Sportiv - Tenis de câmp"
                className="mt-1 bg-white"
              />
              <Label className="text-xs mt-2 block">la (clubul)</Label>
              <Input value={form.club} onChange={(e) => f("club", e.target.value)} className="bg-white" data-testid="adev-gen-club" />
            </div>

            <div className="rounded-md border p-3 bg-white space-y-2">
              <div>
                <Label className="text-xs font-medium">Este suferind(ă) de</Label>
                <Textarea
                  value={form.suferind_de}
                  onChange={(e) => f("suferind_de", e.target.value)}
                  placeholder="Ex: fără afecțiuni / diagnostic sportiv..."
                  rows={2}
                  className="mt-1"
                  data-testid="adev-gen-suferind"
                />
              </div>
              <div>
                <Label className="text-xs font-medium">Se recomandă</Label>
                <Textarea
                  value={form.se_recomanda}
                  onChange={(e) => f("se_recomanda", e.target.value)}
                  placeholder="Ex: continuarea activității sportive..."
                  rows={2}
                  className="mt-1"
                  data-testid="adev-gen-recomanda"
                />
              </div>
              <div>
                <Label className="text-xs font-medium">S-a eliberat prezenta pentru a-i servi la</Label>
                <Input
                  value={form.pentru_a_servi}
                  onChange={(e) => f("pentru_a_servi", e.target.value)}
                  placeholder="Ex: clubul sportiv / federație..."
                  className="mt-1"
                  data-testid="adev-gen-servi"
                />
              </div>
            </div>
          </div>

          {/* Document tipăribil */}
          <div className="adeverinta-generala-printable bg-white p-6 print:p-8" data-testid="adeverinta-generala-doc">
            <div className="flex justify-between items-start mb-4">
              <div className="flex-1">
                <span className="text-sm">Unitatea medicală </span>
                <span className="border-b border-dotted border-slate-700 px-2 text-sm font-medium">
                  {form.unitate_medicala || "\u00A0".repeat(40)}
                </span>
              </div>
              <div className="text-right text-sm">
                <div>Nr.fișă <span className="border-b border-dotted border-slate-700 px-2 font-medium">{form.nr_fisa || "\u00A0".repeat(8)}</span>/<span className="border-b border-dotted border-slate-700 px-2 font-medium">{form.data_fisa || "\u00A0".repeat(10)}</span></div>
              </div>
            </div>

            <h1 className="text-center text-xl font-bold mb-6 tracking-wide">ADEVERINȚĂ MEDICALĂ</h1>

            <div className="space-y-3 text-sm leading-relaxed">
              <p>
                Se adeverește că <span className="border-b border-dotted border-slate-700 px-2 font-medium">{form.nume_complet || "\u00A0".repeat(40)}</span> sexul <span className="border-b border-dotted border-slate-700 px-2 font-medium">{form.sex || "\u00A0"}</span>
              </p>
              <p>
                CNP <span className="border-b border-dotted border-slate-700 px-2 font-medium">{form.cnp || "\u00A0".repeat(20)}</span> Data nașterii <span className="border-b border-dotted border-slate-700 px-2 font-medium">{form.data_nasterii || "\u00A0".repeat(12)}</span>
              </p>
              <p>
                Cu domiciliul în județul <span className="border-b border-dotted border-slate-700 px-2 font-medium">{form.judet || "\u00A0".repeat(15)}</span> Localitatea <span className="border-b border-dotted border-slate-700 px-2 font-medium">{form.localitate || "\u00A0".repeat(20)}</span>
              </p>
              <p>
                Având ocupația de <span className="border-b border-dotted border-slate-700 px-2 font-medium">{form.ocupatia || "\u00A0".repeat(30)}</span> la <span className="border-b border-dotted border-slate-700 px-2 font-medium">{form.club || "\u00A0".repeat(25)}</span>
              </p>
              <p>
                Este suferind(ă) de <span className="border-b border-dotted border-slate-700 px-2 font-medium">{form.suferind_de || "\u00A0".repeat(50)}</span>
              </p>
              <p>
                Se recomandă <span className="border-b border-dotted border-slate-700 px-2 font-medium">{form.se_recomanda || "\u00A0".repeat(60)}</span>
              </p>
              <p>
                S-a eliberat prezenta pentru a-i servi la <span className="border-b border-dotted border-slate-700 px-2 font-medium">{form.pentru_a_servi || "\u00A0".repeat(30)}</span>
              </p>
            </div>

            <div className="flex justify-between items-end mt-12">
              <div>
                <p className="text-sm">Data eliberării:</p>
                <p className="border-b border-dotted border-slate-700 px-2 mt-2 inline-block min-w-[140px] font-medium text-sm">{form.data_eliberarii}</p>
              </div>
              <div className="text-right">
                <p className="text-sm">Semnătura și parafa medicului</p>
                <div className="mt-8 border-b border-dotted border-slate-700 w-56 ml-auto">&nbsp;</div>
              </div>
            </div>
          </div>

          <style>{`
            @media print {
              body * { visibility: hidden; }
              .adeverinta-generala-printable, .adeverinta-generala-printable * { visibility: visible; }
              .adeverinta-generala-printable {
                position: absolute; left: 0; top: 0; width: 100%;
                padding: 1.2cm !important; margin: 0 !important;
                box-shadow: none !important;
                font-size: 16px;
              }
              .adeverinta-generala-printable h1 { font-size: 1.45rem !important; margin-bottom: 0.9rem !important; }
              .adeverinta-generala-printable .mt-12 { margin-top: 1.5rem !important; }
              .adeverinta-generala-printable .mt-8 { margin-top: 1.25rem !important; }
              .adeverinta-generala-printable .mb-6 { margin-bottom: 0.9rem !important; }
              @page { size: A5 portrait; margin: 0; }
            }
          `}</style>
        </DialogContent>
      </Dialog>
    </>
  );
};
